import { motion } from 'framer-motion';
import { TreePine } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    partOf: 'You are now part of',
    livingTree: "'s Living Tree 🌳",
    subtext: 'This message helped their tree grow.',
  },
  es: {
    partOf: 'Ahora eres parte del',
    livingTree: ' Árbol Viviente 🌳',
    subtext: 'Este mensaje ayudó a crecer su árbol.',
  },
  fr: {
    partOf: 'Vous faites maintenant partie de',
    livingTree: ' l\'Arbre Vivant 🌳',
    subtext: 'Ce message a aidé leur arbre à grandir.',
  },
  pt: {
    partOf: 'Você agora faz parte da',
    livingTree: ' Árvore Viva 🌳',
    subtext: 'Esta mensagem ajudou a árvore a crescer.',
  },
  ar: {
    partOf: 'أنت الآن جزء من',
    livingTree: ' شجرة الحياة 🌳',
    subtext: 'هذه الرسالة ساعدت شجرتهم على النمو.',
  },
  ja: {
    partOf: 'あなたは今',
    livingTree: 'のリビングツリーの一部です 🌳',
    subtext: 'このメッセージが彼らの木を育てました。',
  },
  ko: {
    partOf: '당신은 이제',
    livingTree: '의 리빙 트리의 일부입니다 🌳',
    subtext: '이 메시지가 그들의 나무를 성장시켰습니다.',
  },
  id: {
    partOf: 'Kamu sekarang bagian dari',
    livingTree: ' Pohon Kehidupan 🌳',
    subtext: 'Pesan ini membantu pohon mereka tumbuh.',
  },

  hi: {
    partOf: 'आप अब इसका हिस्सा हैं',
    livingTree: "'s Living Tree 🌳",
    subtext: 'इस संदेश ने उनके वृक्ष को बढ़ने में मदद की।',
  },
};

interface LivingTreeConnectionProps {
  senderName?: string;
}

export function LivingTreeConnection({ senderName }: LivingTreeConnectionProps) {
  const { language } = useLanguage();
  const t = lc(copy, language);

  const displayName = senderName || 'Someone';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="py-8 px-4"
    >
      <div className="max-w-sm mx-auto text-center space-y-4">
        {/* Mini tree visualization */}
        <motion.div
          className="relative mx-auto w-32 h-36"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          {/* Tree trunk */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-14 rounded-t-sm bg-gradient-to-t from-amber-800 to-amber-700" />
          
          {/* Tree canopy */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-24">
            {/* Main canopy circle */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-b from-emerald-500/80 to-emerald-700/80 blur-[1px]" />
            <div className="absolute inset-1 rounded-full bg-gradient-to-b from-emerald-400/60 to-emerald-600/60" />
            
            {/* Left canopy extension */}
            <div className="absolute -left-3 top-4 w-12 h-16 rounded-full bg-gradient-to-br from-emerald-500/70 to-emerald-600/70 blur-[1px]" />
            
            {/* Right canopy extension */}
            <div className="absolute -right-3 top-3 w-14 h-14 rounded-full bg-gradient-to-bl from-emerald-400/70 to-emerald-600/70 blur-[1px]" />
          </div>

          {/* Highlighted fruit — the one representing this message */}
          <motion.div
            className="absolute top-6 left-1/2 -translate-x-1/2 z-10"
            animate={{ 
              scale: [1, 1.15, 1],
              boxShadow: [
                '0 0 8px 2px hsla(var(--primary) / 0.3)',
                '0 0 16px 4px hsla(var(--primary) / 0.5)',
                '0 0 8px 2px hsla(var(--primary) / 0.3)',
              ],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-primary to-primary/80 shadow-lg border-2 border-primary-foreground/30" />
          </motion.div>

          {/* Other small fruits */}
          {[
            { x: 8, y: 10 }, { x: 18, y: 4 }, { x: 4, y: 18 },
            { x: 15, y: 16 }, { x: -2, y: 12 },
          ].map((pos, i) => (
            <div
              key={i}
              className="absolute w-2.5 h-2.5 rounded-full bg-gradient-to-br from-emerald-300/60 to-emerald-500/60"
              style={{ left: `calc(50% + ${pos.x}px)`, top: `${pos.y + 4}px` }}
            />
          ))}

          {/* Subtle ground */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-20 h-3 rounded-full bg-emerald-900/20 blur-sm" />
        </motion.div>

        {/* Connection text */}
        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-foreground">
            {t.partOf} <span className="text-primary">{displayName}</span>{t.livingTree}
          </p>
          <p className="text-xs text-muted-foreground/70">
            {t.subtext}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
